import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import AmpliarMixModal from "@/components/AmpliarMixModal";
import { ClipboardList, Camera, MapPin, Plus, ArrowRight, Loader2 } from "@/components/icons";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// PEDIDO_EMERGENT_2A_UMA_PORTA: uma única porta para prospectar, com três
// opções na ordem: Cadastro completo, Foto na rua, Buscar no mapa. O botão
// "+ Novo prospect" da tela Clientes e o FAB do Início passam a abrir esta
// tela em vez do formulário direto.
// LOTE X-1 F2.1: 4º card "Ampliar mix" para abrir o picker de clientes ativos.
export default function Captar() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canFoto = ["admin", "director", "manager", "seller"].includes(user?.role);

  // LOTE X-1 F2.1 — picker do "Ampliar mix" (mesma lógica da antiga Prospectar).
  // LOTE X-1 F2.2 — backend GET /clientes ignora mine/status/q; filtra na tela.
  const [ampliarPicker, setAmpliarPicker] = useState(false);
  const [ampliarCliente, setAmpliarCliente] = useState(null);
  const [ampliarBusca, setAmpliarBusca] = useState("");
  const [ampliarLista, setAmpliarLista] = useState([]);
  const [ampliarLoading, setAmpliarLoading] = useState(false);
  useEffect(() => {
    if (!ampliarPicker) return;
    let cancelled = false;
    setAmpliarLoading(true);
    api.get("/clientes")
      .then((r) => { if (!cancelled) setAmpliarLista(r.data || []); })
      .catch(() => { if (!cancelled) setAmpliarLista([]); })
      .finally(() => { if (!cancelled) setAmpliarLoading(false); });
    return () => { cancelled = true; };
  }, [ampliarPicker]);
  // Filtro inline (sem helper, por decisão da F2.2):
  // 1) só Ativos do próprio usuário (owner_id === user.id);
  // 2) busca local por nome, razão social e CNPJ (com ou sem pontuação).
  const ampliarFiltrada = useMemo(() => {
    const uid = user?.id;
    const base = (ampliarLista || []).filter((c) => c.status === "Ativo" && c.owner_id === uid);
    const q = (ampliarBusca || "").trim().toLowerCase();
    if (!q) return base;
    const qDig = q.replace(/\D/g, "");
    return base.filter((c) => {
      const nome = (c.nome || "").toLowerCase();
      const emp = (c.empresa || c.razao_social || "").toLowerCase();
      const fant = (c.nome_fantasia || "").toLowerCase();
      const cnpjDig = (c.cnpj || "").replace(/\D/g, "");
      if (nome.includes(q) || emp.includes(q) || fant.includes(q)) return true;
      if (qDig && cnpjDig.includes(qDig)) return true;
      return false;
    });
  }, [ampliarLista, ampliarBusca, user?.id]);

  const cards = [
    {
      testId: "captar-cadastro",
      icon: ClipboardList,
      titulo: "Cadastro completo",
      apoio: "Já tem o CNPJ? Cadastre completo.",
      onClick: () => navigate("/prospect/novo"),
      visible: true,
    },
    {
      testId: "captar-foto",
      icon: Camera,
      titulo: "Foto na rua",
      apoio: "Está na frente do estabelecimento? Foto, nome e localização. Complete o CNPJ em até 5 dias.",
      onClick: () => navigate("/captacao"),
      visible: canFoto,
    },
    {
      testId: "captar-mapa",
      icon: MapPin,
      titulo: "Buscar no mapa",
      apoio: "Encontre estabelecimentos perto de você.",
      onClick: () => navigate("/prospectar"),
      visible: true,
    },
    {
      testId: "captar-ampliar-mix",
      icon: Plus,
      titulo: "Ampliar mix de um cliente atual",
      apoio: "Abra uma oportunidade com um cliente que já é seu.",
      onClick: () => setAmpliarPicker(true),
      visible: true,
    },
  ];

  return (
    <div>
      <PageHeader title="Prospectar" subtitle="Escolha como registrar um novo prospect" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.filter((c) => c.visible).map((c) => (
          <button
            key={c.testId}
            onClick={c.onClick}
            data-testid={c.testId}
            className="text-left bg-card rounded-2xl border border-black/5 p-6 shadow-[0_2px_10px_rgb(0,0,0,0.03)] hover:shadow-[0_10px_30px_rgb(0,0,0,0.07)] hover:-translate-y-0.5 transition-all"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-brand-soft flex items-center justify-center shrink-0">
                <c.icon className="w-6 h-6 text-brand" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-base font-semibold text-navy flex items-center gap-2">
                  {c.titulo} <ArrowRight className="w-4 h-4 text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground mt-1">{c.apoio}</p>
              </div>
            </div>
          </button>
        ))}
      </div>

      <Dialog open={ampliarPicker} onOpenChange={setAmpliarPicker}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Escolha o cliente para ampliar o mix</DialogTitle>
          </DialogHeader>
          <div className="space-y-3" data-testid="ampliar-mix-picker">
            <input
              value={ampliarBusca}
              onChange={(e) => setAmpliarBusca(e.target.value)}
              placeholder="Buscar cliente ativo..."
              data-testid="ampliar-mix-picker-busca"
              className="w-full h-10 px-3 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40"
              autoFocus
            />
            <div className="max-h-72 overflow-y-auto border border-input rounded-lg">
              {ampliarLoading ? (
                <div className="p-4 text-xs text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Carregando…</div>
              ) : ampliarFiltrada.length === 0 ? (
                <div className="p-4 text-xs text-muted-foreground" data-testid="ampliar-mix-picker-vazio">
                  {ampliarBusca ? "Nenhum cliente ativo encontrado." : "Você não tem clientes ativos na sua carteira."}
                </div>
              ) : (
                ampliarFiltrada.slice(0, 50).map((c) => (
                  <button
                    key={c.id}
                    onClick={() => { setAmpliarCliente({ id: c.id, nome: c.nome }); setAmpliarPicker(false); }}
                    data-testid={`ampliar-mix-picker-item-${c.id}`}
                    className="w-full text-left px-3 py-2.5 border-b last:border-b-0 border-slate-100 hover:bg-brand-soft/40"
                  >
                    <div className="text-sm font-medium text-navy truncate">{(c.nome_fantasia && c.nome_fantasia.trim()) || c.nome}</div>
                    {c.nome_fantasia && c.nome_fantasia.trim() && c.nome && c.nome !== c.nome_fantasia && (
                      <div className="text-[11px] text-muted-foreground truncate">{c.razao_social || c.nome}</div>
                    )}
                    {c.cidade && <div className="text-xs text-muted-foreground">{c.cidade}</div>}
                  </button>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AmpliarMixModal
        open={!!ampliarCliente}
        onOpenChange={(v) => { if (!v) setAmpliarCliente(null); }}
        cliente={ampliarCliente || { id: "", nome: "" }}
      />
    </div>
  );
}
